/**
 * Shared zod schemas + inferred TS types for the Phase-5 dashboard.
 *
 * This module is the single source of truth for the
 * `GET /api/v1/dashboard/kpi-summary` envelope and its sub-shapes.
 * It is consumed both by:
 *   - `apps/api`  — the controller pre-validates / strict-parses the
 *     response shape via `DashboardKpiSummarySchema.parse(body)`.
 *   - `apps/web`  — the FE hook `useDashboardKpiSummary` performs a
 *     runtime parse on every fetch (DA SER-9 / round-2 / R-10).
 *
 * Originally landed local to the API package as `dto/kpi-summary.dto.ts`
 * (Phase 5 W2.T2.1 pragmatic compromise). Lifted to `@aris/shared` per
 * PLAN T2.3 so the FE can depend on the inferred TS types directly,
 * eliminating duplicate parse code across the boundary.
 *
 * Field semantics decoded from
 *   - `.planning/phases/05-dashboard/research-backend.md` §3.1
 *   - `.planning/phases/05-dashboard/CONTEXT.md` FI-5..FI-12
 *   - `.planning/phases/05-dashboard/PLAN.md` T2.1 step 7
 *
 * Verdict thresholds (FI-5 round-2): teamLoad.verdict resolves to
 *   - OVERLOAD  if percent > 105
 *   - IN_NORM   if 95 ≤ percent ≤ 105
 *   - UNDERLOAD if percent < 95
 * where percent = actualHours / targetHours * 100, rounded to 1 decimal.
 *
 * Per-employee verdict (FI-5 strict `>`): hours > 40 → OVERLOAD; ≤ 40 → IN_NORM.
 *
 * **Strict objects.** Every shape uses `.strict()` so unknown keys throw
 * at parse time — this is the trip-wire that catches BE/FE drift the
 * moment a new field appears in the wire payload but is not yet declared
 * in this file.
 */
import { z } from 'zod';

// ─── Primitives ───────────────────────────────────────────────────────────
// Inlined rather than re-exported so consumers can `.parse()` without
// importing helpers from elsewhere in the shared bundle.
const Uuid = z.string().uuid();
const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const IsoDateTime = z.string().datetime({ offset: true });

// ─── Verdicts ────────────────────────────────────────────────────────────
export const TeamLoadVerdictSchema = z.enum(['OVERLOAD', 'IN_NORM', 'UNDERLOAD']);
export type TeamLoadVerdict = z.infer<typeof TeamLoadVerdictSchema>;

export const EmployeeLoadStatusSchema = z.enum(['OVERLOAD', 'IN_NORM']);
export type EmployeeLoadStatus = z.infer<typeof EmployeeLoadStatusSchema>;

// ─── Sub-shapes ──────────────────────────────────────────────────────────

/** A single employee's weekly hours roll-up. */
export const EmployeeWeekRowSchema = z
  .object({
    employeeId: Uuid,
    fullName: z.string().min(1),
    roleName: z.string().nullable(),
    departmentId: z.number().int().positive().nullable(),
    hours: z.number().nonnegative(),
    /** Strict `> 40` per FI-5 — boundary 40.0h is IN_NORM. */
    status: EmployeeLoadStatusSchema,
  })
  .strict();
export type EmployeeWeekRow = z.infer<typeof EmployeeWeekRowSchema>;

/** Compact project row for the active-projects panel + deadlines KPI. */
export const DashboardProjectSummarySchema = z
  .object({
    id: Uuid,
    name: z.string().min(1),
    code: z.string().nullable(),
    deadline: IsoDate.nullable(),
    /** Days until deadline; negative if overdue. Null if no deadline. */
    daysToDeadline: z.number().int().nullable(),
    clientName: z.string(),
    /** Compact ГИП projection so the FE doesn't need to fan out. */
    gip: z.object({ id: Uuid, fullName: z.string() }).strict(),
    participantCount: z.number().int().nonnegative(),
  })
  .strict();
export type DashboardProjectSummary = z.infer<typeof DashboardProjectSummarySchema>;

// ─── KPI panels ──────────────────────────────────────────────────────────

export const TeamLoadKpiSchema = z
  .object({
    /** Sum of target weekly hours across the active cohort. */
    targetHours: z.number().nonnegative(),
    /** Sum of actual fact hours across the active cohort (matview). */
    actualHours: z.number().nonnegative(),
    /** Cohort headcount = live `User.findMany({where: {dept, active}})` size. */
    headcount: z.number().int().nonnegative(),
    /** % of plan = actualHours / targetHours * 100, 1-decimal-rounded.
     *  Null when headcount === 0 (avoids divide-by-zero footgun on FE). */
    percent: z.number().nonnegative().nullable(),
    verdict: TeamLoadVerdictSchema,
  })
  .strict();
export type TeamLoadKpi = z.infer<typeof TeamLoadKpiSchema>;

export const OverloadedKpiSchema = z
  .object({
    /** Count of cohort employees whose weekly hours > 40. */
    count: z.number().int().nonnegative(),
    /** All overloaded employees, sorted hours desc; FE truncates display. */
    employees: z.array(EmployeeWeekRowSchema),
  })
  .strict();
export type OverloadedKpi = z.infer<typeof OverloadedKpiSchema>;

export const DeadlinesKpiSchema = z
  .object({
    /** Count of active projects with deadline ∈ [today, today+30d]. */
    count: z.number().int().nonnegative(),
    /** All such projects, sorted by daysToDeadline asc; FE colours per FI-6. */
    projects: z.array(DashboardProjectSummarySchema),
  })
  .strict();
export type DeadlinesKpi = z.infer<typeof DeadlinesKpiSchema>;

export const EmployeeLoadPanelSchema = z
  .object({
    /** Top 5 by hours desc — ADMIN org-wide, MANAGER dept-scoped. */
    topFive: z.array(EmployeeWeekRowSchema),
    /** Bottom 5 by hours asc — same scope. */
    bottomFive: z.array(EmployeeWeekRowSchema),
    /**
     * Phase 5 fix-pack FA-2 (closes F-20). Full department cohort sorted
     * by hours desc — populated ONLY for MANAGER scope so the FE «Полная
     * нагрузка отдела» table renders the entire dept (>5 employees) and
     * not just `topFive`. ADMIN scope keeps this null because the org-
     * wide cohort can be hundreds of rows; the top/bottom-5 panels remain
     * the canonical ADMIN surface.
     */
    fullList: z.array(EmployeeWeekRowSchema).nullable(),
  })
  .strict();
export type EmployeeLoadPanel = z.infer<typeof EmployeeLoadPanelSchema>;

export const StalenessSchema = z
  .object({
    /** ISO-8601 timestamp of `dashboard_matview_meta.last_refresh`. */
    refreshedAt: IsoDateTime,
    /** Age in seconds at request time = NOW() - last_refresh. */
    ageSeconds: z.number().int().nonnegative(),
  })
  .strict();
export type Staleness = z.infer<typeof StalenessSchema>;

// ─── Области ответа (Блок 2 · Ф8 · В-Б1) ─────────────────────────────────

/**
 * По чему посчитана часть ответа.
 *
 *   `ORG`          — всё бюро;
 *   `DEPARTMENT`   — отдел актора;
 *   `OWN_PROJECTS` — «свои объекты»: объекты, где актор действует за ГИПа (сам
 *                    ГИП либо его помощник), и люди — участники этих объектов.
 *
 * Основание — `DECISIONS.md`: **Р-196** (плитки — по праву «показатели бюро на
 * дашборде», поимённые часы — по праву «вся загруженность бюро», части
 * независимые) и **Р-214** (администратор с отнятым дашбордом бюро: дашборд
 * остаётся, плитки — по объектам, где он ГИП).
 */
export const DashboardScopeSchema = z.enum(['ORG', 'DEPARTMENT', 'OWN_PROJECTS']);
export type DashboardScope = z.infer<typeof DashboardScopeSchema>;

// ─── Envelope ────────────────────────────────────────────────────────────

export const DashboardKpiSummarySchema = z
  .object({
    /** ISO date (YYYY-MM-DD) of the Monday boundary of the current ISO week. */
    weekStart: IsoDate,
    /** ISO date of the «today» reference used for deadlines + week end. */
    weekEnd: IsoDate,
    /**
     * "ORG" | "DEPARTMENT" — narrowed scope echoed back so FE can display.
     *
     * ⚠️ ПРЕЖНЕЕ ПОЛЕ, ОСТАВЛЕНО РАДИ ВЕБА ДО ВОЛНЫ В-Б2 — новому коду читать
     * `tilesScope`. Третьего значения оно не получило намеренно: тип поля
     * читает заголовок страницы, и расширение уронило бы сборку веба, который
     * волной В-Б1 не правится. При виде «свои объекты» здесь стоит
     * `'DEPARTMENT'` с `departmentId: null` — чтобы прежний заголовок не
     * подписал «Вся компания» над числами по нескольким объектам.
     */
    scope: z.enum(['ORG', 'DEPARTMENT']),
    /** Department id when scope === 'DEPARTMENT', else null. */
    departmentId: z.number().int().positive().nullable(),
    /**
     * Область ПЛИТОК — `teamLoad`, `deadlines`, `activeProjects` (право № 8).
     *
     * Необязательно в схеме только ради ответов и фикстур, записанных до
     * В-Б1; сервер отдаёт поле всегда.
     */
    tilesScope: DashboardScopeSchema.optional(),
    /**
     * Область ЛЮДЕЙ — `overloaded`, `employeeLoad` (право № 7). От области
     * плиток НЕ зависит: у руководителя с одним добавленным правом они разные.
     *
     * ⚠️ `null` — «поимённого актору не положено» (Р-208): `overloaded` и
     * `employeeLoad` в ответе при этом ЕСТЬ и ПУСТЫ. Пустые списки при `null`
     * — не «никто не перегружен», а «не показано»: рисовать эти блоки нельзя.
     * Поля не убраны из ответа потому же, почему `scope` не получил третьего
     * значения, — их без проверки читает веб до В-Б2.
     */
    peopleScope: DashboardScopeSchema.nullable().optional(),
    teamLoad: TeamLoadKpiSchema,
    overloaded: OverloadedKpiSchema,
    deadlines: DeadlinesKpiSchema,
    employeeLoad: EmployeeLoadPanelSchema,
    /** Active projects panel (separate from deadlines KPI). */
    activeProjects: z.array(DashboardProjectSummarySchema),
    /** Matview staleness sidecar — FE mirrors `X-Aris-Dashboard-Stale` header. */
    stale: StalenessSchema,
  })
  .strict();
export type DashboardKpiSummary = z.infer<typeof DashboardKpiSummarySchema>;
